// ──────────────────────────────────────────────────────────────────────────
// Toaster — drop-in replacement for the dashboard's many alert() calls.
//
//   import { toast, Toaster } from './components/Toaster';
//   toast.success('Saved!');           // emits a toast from anywhere
//   toast.error('Something broke');    // even from non-React code
//   toast.info('Heads-up');
//   toast.warning('Careful');
//
//   <Toaster />                        // mount once, somewhere near the top
//
// Implementation note: we use a tiny module-level event bus so any helper
// (axios catch handler, async function, etc.) can fire a toast without
// having to plumb a context through the React tree.
// ──────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { CheckCircle, AlertTriangle, Info, X, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// ─── event bus ────────────────────────────────────────────────────────────
const listeners = new Set();
let nextId = 1;

const emit = (toastObj) => {
  listeners.forEach((fn) => fn(toastObj));
};

const make = (variant) => (message, opts = {}) => {
  if (!message) return;
  emit({
    id: nextId++,
    variant,                           // 'success' | 'error' | 'info' | 'warning'
    title: opts.title || null,
    message: String(message),
    duration: opts.duration ?? 4500,   // ms before auto-dismiss; 0 = sticky
  });
};

export const toast = {
  success: make('success'),
  error: make('error'),
  info: make('info'),
  warning: make('warning'),
};

// ─── visuals ──────────────────────────────────────────────────────────────
const VARIANT = {
  success: { color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0', Icon: CheckCircle },
  error:   { color: '#ef4444', bg: '#fef2f2', border: '#fecaca', Icon: AlertCircle },
  warning: { color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', Icon: AlertTriangle },
  info:    { color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe', Icon: Info },
};

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(20px) scale(0.96); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
`;

const slideOut = keyframes`
  from { opacity: 1; transform: translateX(0) scale(1); max-height: 200px; margin-bottom: 0.65rem; padding-top: 0.85rem; padding-bottom: 0.85rem; }
  to   { opacity: 0; transform: translateX(20px) scale(0.96); max-height: 0; margin-bottom: 0; padding-top: 0; padding-bottom: 0; }
`;

const progress = keyframes`
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
`;

const Stack = styled.div`
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 99999;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  width: min(380px, calc(100vw - 2rem));
  pointer-events: none;
`;

const Card = styled.div`
  pointer-events: auto;
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.85rem 0.95rem;
  padding-right: 2.5rem;
  background: ${p => p.$v.bg};
  color: #0f172a;
  border: 1px solid ${p => p.$v.border};
  border-radius: 12px;
  box-shadow:
    0 10px 25px -8px rgba(15, 23, 42, 0.18),
    0 4px 10px -4px rgba(15, 23, 42, 0.08);
  font-size: 0.875rem;
  line-height: 1.35;
  overflow: hidden;
  animation: ${p => p.$leaving ? slideOut : slideIn} ${p => p.$leaving ? '260ms' : '220ms'} cubic-bezier(0.4, 0, 0.2, 1) both;
`;

const IconWrap = styled.div`
  flex-shrink: 0;
  margin-top: 1px;
  color: ${p => p.$v.color};
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.1rem;
`;

const Message = styled.div`
  color: #334155;
  word-break: break-word;
`;

const Close = styled.button`
  position: absolute;
  top: 0.55rem;
  right: 0.55rem;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover { background: rgba(15, 23, 42, 0.06); color: #0f172a; }
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 3px;
  transform-origin: left center;
  background: ${p => p.$v.color};
  opacity: 0.65;
  animation: ${progress} ${p => p.$dur}ms linear forwards;
`;

// ─── Toaster component ───────────────────────────────────────────────────
export const Toaster = () => {
  const { t: tr } = useTranslation();
  const [items, setItems] = useState([]);            // {id, variant, title, message, duration, leaving}
  const timeouts = useRef(new Map());

  const dismiss = useCallback((id) => {
    setItems((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    // remove after exit animation
    setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id));
      const h = timeouts.current.get(id);
      if (h) clearTimeout(h);
      timeouts.current.delete(id);
    }, 260);
  }, []);

  useEffect(() => {
    const onToast = (t) => {
      setItems((prev) => [...prev, { ...t, leaving: false }]);
      if (t.duration > 0) {
        const h = setTimeout(() => dismiss(t.id), t.duration);
        timeouts.current.set(t.id, h);
      }
    };
    listeners.add(onToast);
    return () => {
      listeners.delete(onToast);
      timeouts.current.forEach((h) => clearTimeout(h));
      timeouts.current.clear();
    };
  }, [dismiss]);

  if (items.length === 0) return null;

  return (
    <Stack role="status" aria-live="polite">
      {items.map((t) => {
        const v = VARIANT[t.variant] || VARIANT.info;
        const Icon = v.Icon;
        return (
          <Card key={t.id} $v={v} $leaving={!!t.leaving}>
            <IconWrap $v={v}><Icon size={18} /></IconWrap>
            <Body>
              {t.title && <Title>{t.title}</Title>}
              <Message>{t.message}</Message>
            </Body>
            <Close onClick={() => dismiss(t.id)} aria-label={tr('toaster.dismiss')}>
              <X size={14} />
            </Close>
            {t.duration > 0 && !t.leaving && <ProgressBar $v={v} $dur={t.duration} />}
          </Card>
        );
      })}
    </Stack>
  );
};

export default Toaster;
